import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import {
  DynamicTableColDef,
  FlyoutMenu,
  FrkBreakpoint,
  ResponsiveService,
  StickyFooterActionParam,
  ToggleButton,
} from '@frk/eds-components';
import { WINDOW } from '@ng-web-apis/common';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import {
  ColumnDefs,
  TaxInfoDownloadType,
  TaxInfoPrintType,
  TaxInfoStickySelectEvent,
  TaxInfoToggleValues,
  TaxListInfoAction,
  TaxListInfoFilterType,
  TaxListInfoListing,
  TaxListInfoRecordDetails,
  TaxListInfoSelectedFilters,
  TaxListInfoState,
} from '@products/types/tax-list-info.type';
import {
  IUserProfile,
  PersonalisationAPIService,
  ProfileService,
  SegmentService,
  SignInService,
} from '@services';
import { SignInInterface } from '@shared/sign-in/sign-in.interface';
import { TranslateService } from '@shared/translate/translate.service';
import {
  PersonalisationPersonalData,
  ProductDetailConfiguration,
  SegmentId,
} from '@types';
import { Logger } from '@utils/logger';
import cloneDeep from 'lodash/cloneDeep';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { TaxListInfoService } from '../services/tax-list-info.service';
import { TaxDisplayComponent } from '../tax-component.interface';
import { TaxConfig } from '../tax.config';
import { NgxSpinnerService } from 'ngx-spinner';

const logger = Logger.getLogger('TaxListInfoComponent');

@Component({
  selector: 'ft-tax-list-info',
  templateUrl: './tax-list-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxListInfoComponent
  extends BaseProductDetailComponent
  implements TaxDisplayComponent, OnInit, OnDestroy {
  private componentConfiguration: TaxConfig;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private selectedFilters: TaxListInfoSelectedFilters;
  private isHomeOfficeUser = false;
  private isHomeOfficeUser$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  public isHandheld$: Observable<boolean>;
  public isPopulated = false;
  public filters: TaxListInfoFilterType[];
  public recordDetails: TaxListInfoRecordDetails;
  public currentPage$: Observable<TaxListInfoListing[]>;
  public colDefs: DynamicTableColDef<TaxListInfoListing>[];
  public FrkBreakpoint = FrkBreakpoint;
  public rowCount = 0;
  public rowCountValue = '';
  public printStickyButton: StickyFooterActionParam;
  public downloadStickyButton: StickyFooterActionParam;
  public downloadType = TaxInfoDownloadType;
  public downloadTypesMenu: Array<FlyoutMenu> = [];
  public isCardView$: Observable<boolean>;
  public printableRecords: TaxListInfoListing[];
  public isPrintEnable = false;
  public printType = TaxInfoPrintType;
  public expandAll = false;
  public taxYear = '';

  public proximalPrintTop: string;
  public proximalPrintBottom: string;
  public taxInfoToggleButtons: ToggleButton[] = [];

  public activeToggle = '';
  public signInInputParameters: SignInInterface;
  public title: string;
  public subtitle: string;
  public isProfileActivated$: Observable<boolean>;
  public resendEmail: boolean;
  public registerError: boolean;
  public isIdentified: boolean;
  public isRecordsAvailable: boolean;
  public TaxInfoToggleValues = TaxInfoToggleValues;
  public isLoggedIn$: Observable<boolean>;
  public isShowEmptyState: boolean;
  public proximalTopColumnNumbers: ColumnDefs;
  public isSegmentFP: boolean;

  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    private taxListInfoService: TaxListInfoService,
    private changeDetector: ChangeDetectorRef,
    private responsiveService: ResponsiveService,
    private translateService: TranslateService,
    private profileService: ProfileService,
    private personalisationService: PersonalisationAPIService,
    private segmentService: SegmentService,
    private signInService: SignInService,
    private spinner: NgxSpinnerService
  ) {
    super();
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.isCardView$ = this.responsiveService.isBreakpointOrBelow$(
      FrkBreakpoint.Tablet
    );
    this.personalisationService
      .isIdentified$()
      .subscribe((isIdentified) => (this.isIdentified = isIdentified));

    // read current segment
    this.segmentService
      .getCurrentSegmentId$()
      .pipe(takeUntil(this.unsubscribe$), take(1))
      .subscribe((currentSegmentId: SegmentId) => {
        this.isSegmentFP =
          currentSegmentId === SegmentId.FINANCIAL_PROFESSIONALS;
      });

    this.isLoggedIn$ = this.profileService.isLoggedIn$();
    // listen for register modal after successful registration
    this.isProfileActivated$ = this.personalisationService.isProfileActivated$();
  }

  populate(
    componentConfig: TaxConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ): void {
    this.initialize(brConfig, component, page);
    this.spinner.show('taxListInfoSpinner');

    logger.debug(page, this.page);
    this.componentConfiguration = componentConfig;
    combineLatest([
      this.profileService.getUserProfile$(),
      this.isHomeOfficeUser$,
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([profile, isHomeOfficeUser]: [IUserProfile, boolean]) => {
        this.isHomeOfficeUser = isHomeOfficeUser;
        this.taxInfoToggleButtons = [
          {
            active: false,
            text: this.translateService.instant(
              'products.us-tax-info-all-funds'
            ),
            value: TaxInfoToggleValues.ALL_FUNDS,
          },
        ];
        if (!profile.profileInfo?.nonRegFP) {
          this.taxInfoToggleButtons.push({
            active: false,
            text: this.isHomeOfficeUser
              ? this.translateService.instant(
                  'products.us-tax-info-home-office-products-title'
                )
              : this.translateService.instant(
                  'products.us-tax-info-featured-strategies-title'
                ),
            value: TaxInfoToggleValues.HELD_PRODUCTS,
          });
        }
        this.taxInfoToggleButtons.push({
          active: false,
          text: this.translateService.instant(
            'products.us-tax-info-focus-list-title'
          ),
          value: TaxInfoToggleValues.FAVORITE_PRODUCTS,
        });
        this.setInitialData();
      });
    this.personalisationService
      .getPersonalData$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((personalData: PersonalisationPersonalData) => {
        this.isHomeOfficeUser$.next(
          this.personalisationService.isHomeOfficeUser(personalData.clientType)
        );
      });
    this.signInInputParameters = {
      skin: 'Basic',
      legacy: false,
      signOut: false,
      showLegacyContent: false,
      subTitle: ' ',
      signInBodyContent: ' ',
      signInLegacyContent: ' ',
    };

    if (this.brConfig) {
      this.colDefs = [
        {
          field: 'fundName',
          headerName: this.translateService.instant(
            `products.us-tax-info-page-fundname`
          ),
          isCardTitle: true,
          linkField: 'link',
          sortable: true,
        },
        {
          field: 'directUSGovernmentObligations',
          headerName: this.translateService.instant(
            `products.us-tax-info-page-directusgovernmentobligations`
          ),
          dataType: 'percent',
          sortable: true,
        },
        {
          field: 'dividendsReceivedDeduction',
          headerName: this.translateService.instant(
            `products.us-tax-info-page-dividendsreceiveddeduction`
          ),
          dataType: 'percent',
          sortable: true,
        },
        {
          field: 'foreignSourceIncome',
          headerName: this.translateService.instant(
            `products.us-tax-info-page-foreignsourceincome`
          ),
          dataType: 'percent',
          sortable: true,
        },
        { field: '', headerName: '' },
      ];

      this.taxListInfoService
        .getTaxListInfoState$()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (state: TaxListInfoState) => {
            logger.debug('Tax List Info state: ', state);
            this.isPopulated = state.isLoaded;
            if (this.isPopulated) {
              if (state.action === TaxListInfoAction.INITIAL_LOAD) {
                this.filters = cloneDeep(state.filters);
              }
              this.isRecordsAvailable = state.recordDetails.total > 0;
              this.recordDetails = cloneDeep(state.recordDetails);
              this.taxYear = state.taxYear;
            }
            this.isShowEmptyState =
              !this.isRecordsAvailable && this.isPopulated;
            this.setDownloadMenuItems();
            this.spinner.hide('taxListInfoSpinner');
            this.changeDetector.detectChanges();
          },
          (error) => {
            logger.debug('Error in fetching state: ', error);
            this.isPopulated = false;
            this.changeDetector.detectChanges();
          }
        );

      this.taxListInfoService
        .getTotalSelectedRows$()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((count: number) => {
          this.rowCount = count;
          this.rowCountValue = `${
            this.rowCount
          } ${this.translateService.instant(
            'products.us-tax-info-page-sticky-item'
          )}`;
          this.changeDetector.detectChanges();
        });

      this.windowRef.onafterprint = (e: Event) => {
        this.printableRecords = [];
        this.isPrintEnable = false;
        this.changeDetector.detectChanges();
      };
      // Get current page records as observable.
      this.currentPage$ = this.taxListInfoService.getCurrentPage$();
    }
  }

  /**
   * set the initial title and toggle based on the segment and query params
   */
  private setInitialData(): void {
    const searchParams = new URLSearchParams(this.windowRef.location.search);
    this.activeToggle = searchParams.get('selection');

    if (!this.isSegmentFP) {
      this.taxListInfoService.populate();
    } else {
      this.taxListInfoService.populate(this.activeToggle);
    }
    // set title for active toggle on page load
    this.setTitle(this.activeToggle);
    this.setTaxInfoToggleButtonsActive();
  }

  /**
   * set download menu items based on the data present
   */
  private setDownloadMenuItems(): void {
    this.downloadTypesMenu = [];
    if (this.isShowEmptyState) {
      this.downloadTypesMenu = [
        {
          link: this.translateService.instant(
            `products.us-tax-info-excel-download-all`
          ),
        },
      ];
    } else {
      this.downloadTypesMenu = [
        {
          link: this.translateService.instant(
            `products.us-tax-info-excel-download-all`
          ),
        },
        {
          link: this.translateService.instant(
            `products.us-tax-info-excel-download-displayed`
          ),
        },
      ];
    }
  }

  /**
   * Trigger grid filter as per the filter selection(s).
   * @param selectedFilters selected dropdown or searched text
   */
  public onFilterApplied(selectedFilters: TaxListInfoSelectedFilters): void {
    this.selectedFilters = selectedFilters;

    // And, send the updated details for further grid filter processes.
    this.taxListInfoService.filterListing(selectedFilters, this.activeToggle);

    this.expandAll = this.selectedFilters?.selectedDropdowns?.some(
      (value) => value.filterName === 'taxDataPoint'
    );
  }

  /**
   * Triggers download for selected option
   * @param downloadType selected option from flyoput menu
   */
  public onDownloadAction(downloadType?: TaxInfoDownloadType): void {
    this.taxListInfoService.handleDownloadAction(downloadType);
  }

  /**
   * Handle event on select and un select checkbox
   * @param selectEvent triggered event
   */
  public onChangeSelection(selectEvent?: TaxInfoStickySelectEvent): void {
    this.taxListInfoService.updateSelection(selectEvent);

    this.printStickyButton = {
      hasAction: true,
      buttonLabel: this.translateService.instant(
        'products.us-tax-info-page-sticky-print'
      ),
      iconType: 'print',
    };
    this.downloadStickyButton = {
      hasAction: true,
      buttonLabel: this.translateService.instant(
        'products.us-tax-info-page-sticky-download'
      ),
      iconType: 'download',
    };
  }
  /**
   * Print functionality to be implemented TODO
   */
  public print(
    printType?: TaxInfoPrintType,
    taxListInfo?: TaxListInfoListing
  ): void {
    if (printType === TaxInfoPrintType.SELECTED) {
      this.printableRecords = this.taxListInfoService.getSelectedTaxInfo();
    } else {
      this.printableRecords = [taxListInfo];
    }
    this.isPrintEnable = true;
    this.changeDetector.detectChanges();
  }

  /**
   * update toggle click
   * @param value - active toggle value
   */
  public updateToggle(value: string) {
    this.activeToggle = value;
    this.setTitle(value);
    this.setTaxInfoToggleButtonsActive();
    switch (value) {
      case TaxInfoToggleValues.HELD_PRODUCTS:
        this.taxListInfoService.setFeaturedList(this.activeToggle);
        break;
      case TaxInfoToggleValues.FAVORITE_PRODUCTS:
        this.taxListInfoService.setFavoriteList(this.activeToggle);
        break;
      default:
        this.taxListInfoService.setAllFunds(this.activeToggle);
        break;
    }
  }

  /**
   * set the title and subtitle for active toggle
   * @param value - active toggle value
   */
  private setTitle(value: string) {
    switch (value) {
      case TaxInfoToggleValues.HELD_PRODUCTS:
        this.title = this.isHomeOfficeUser
          ? this.translateService.instant(
              'products.us-tax-info-home-office-products-title'
            )
          : this.translateService.instant(
              'products.us-tax-info-featured-strategies-title'
            );
        this.subtitle = this.isHomeOfficeUser
          ? this.translateService.instant(
              'products.us-tax-info-home-office-products-subtitle'
            )
          : this.translateService.instant(
              'products.us-tax-info-featured-strategies-subtitle'
            );
        break;
      case TaxInfoToggleValues.FAVORITE_PRODUCTS:
        this.title = this.translateService.instant(
          'products.us-tax-info-focus-list-title'
        );
        this.subtitle = this.translateService.instant(
          'products.us-tax-info-focus-list-subtitle'
        );
        break;
      default:
        this.title = this.translateService.instant(
          'products.us-tax-info-all-funds'
        );
        this.subtitle = '';
        break;
    }
  }

  /**
   * Sets the active state for the tax info toggle buttons.
   *
   * This method finds the index of the currently active toggle button
   * and sets its `active` property to `true`. If no active toggle is found,
   * the first toggle button is set to active by default.
   *
   * @private
   */
  private setTaxInfoToggleButtonsActive(): void {
    let selectedToggleIndex = 0;
    if (this.activeToggle) {
      selectedToggleIndex = this.taxInfoToggleButtons.findIndex(
        (button: ToggleButton) => button.value === this.activeToggle
      );
    }
    this.taxInfoToggleButtons[selectedToggleIndex].active = true;
  }

  /**
   * redirect the user to auth0 signup page
   */
  public goToAuth0(): void {
    this.signInService.submitAuth0('signup');
    logger.debug('Go to auth0 signup screen');
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
